import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { sendMessage } from "../../pages/chat/AgoraMethods";
import { getInitials, getToday } from "../../../util/helpers";
import { TimeDividerSticky, generateRTMUI, generateRTMUI1 } from "../../pages/chat/ChatTexting";
import PatientLoader from "../PatientLoader";
import RiskPill from "../RiskPill";
import { ChatInputComponent } from "../../pages/chat/ChatInputComponent";

import { ReactComponent as ChatIcon } from "../../../assets/icons/Chat.svg";
import { ReactComponent as MiniChatIcon } from "../../../assets/icons/ChatMessageIcon.svg";
import { ReactComponent as DownChevronIcon } from "../../../assets/icons/ChatDownChevron.svg";
import { CBadge } from "@coreui/react";
import "./StickyChat.css";
import { markAsReadLogic } from "../../../store/features/chat/chatSlice";
import { Lightbox } from "react-modal-image";

const StickyChat = ({
  user,
  setExpanded,
  unreadCount = null,
  getPatientMessages = null,
}) => {
  return (
    <div
      className="sticky-chat-collapsed"
      onClick={() => {
        setExpanded(true);

        if (getPatientMessages) {
          getPatientMessages();
        }
      }}
    >
      <ChatIcon width="24px" height="23px" /> <strong>{user}'s Messages</strong>{" "}
      <CBadge
        shape="rounded-circle"
        style={{ color: "white", backgroundColor: "red", padding: "5px 10px" }}
      >
        {unreadCount > 0 && unreadCount}
      </CBadge>
    </div>
  );
};

export const PatientChatBubble = ({ chatData, avatar = null }) => {
  const time = moment(chatData?.modified).format("hh:mm A");

  let url = `https://placehold.co/70x70/f1f1f5/395E9D/?text=${getInitials(
    chatData?.sentBy
  )}`;

  if (avatar) {
    url = avatar;
  }
  return (
    <div
      className="chat-container"
      style={{
        flexDirection: chatData?.type === "outgoing" ? "row-reverse" : "row",
      }}
    >
      <img
        alt="User's avatar"
        style={{ height: "40px", width: "42px", borderRadius: "100%" }}
        src={url}
      />
      <div className={`${chatData?.type}-chat`}>
        <small>{chatData?.sentBy}</small>
        {chatData?.message_type === "text" && <span>{chatData?.message}</span>}
        {chatData?.message_type === "image" && (
          <img alt="User sent you a snap" src={chatData?.message} />
        )}
      </div>
      <span className="timestamp">{time}</span>
    </div>
  );
};

const getFlagText = (flag) => {
  const flagLookup = {
    red: {
      text: "Redflag Patient",
      type: "urgent",
    },
    yellow: {
      text: "Yellowflag Patient",
      type: "medium",
    },
    green: {
      text: "Greenflag Patient",
      type: "low",
    },
    default: {
      text: "",
      type: "",
    },
  };

  return flagLookup[flag] || flagLookup["default"];
};

export const MiniChat = ({
  setExpanded,
  inChatMessage,
  setInChatMessage,
  setUnreadCount,
  chatHistory = [],
  chatData = {},
  getPatientMessages = null,
}) => {
  const { user,selectedProvider } = useSelector((state) => state.app);
  const { singlePatientData } = useSelector((state) => state.patient);
  const flag = getFlagText(singlePatientData?.flag?.toLowerCase() || "default");
  const [clinicianChatMessage, setClinicianChatMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewURL, setPreviewURL] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [lastKnownDate, setLastKnownDate] = useState(new Date());
  const [messages, setMessages ] = useState([]);
  const todays = getToday();
  const latestMessageRef = useRef(null);

  const handlePreview = (url) => {
    setPreviewURL(url.replace(/\[.*?\]/g, '').trim());
  };
  useEffect(() => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  useEffect(() => {
    if (previewURL) {
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  }, [previewURL]);

  useEffect(() => {
    if (chatHistory && chatHistory.length > 0) {
      const sortedChatHistory = [...chatHistory].sort((a, b) => new Date(a.created) - new Date(b.created));
      let seenToday = false;
      let isFirstMessage = true;
      const newMessages = [];

      sortedChatHistory.forEach((each) => {
        const condition2 =
          moment(new Date(each?.created)).format("MMMM DD, yyyy") !==
          moment(lastKnownDate).format("MMMM DD, yyyy");

        const condition3 =
          moment(new Date(each?.created)).format("MMMM DD, yyyy") ===
          moment(todays).format("MMMM DD, yyyy");
        const condition4 =
          moment(new Date(each?.created)).format("MMMM DD, yyyy") ===
          moment(lastKnownDate).format("MMMM DD, yyyy");
        let dp = null;
        if (each?.type === "incoming") {
          if (chatData?.profile_picture) {
            dp = chatData?.profile_picture;
          }
        }

        if ((!lastKnownDate || condition2) && !condition3) {
          setLastKnownDate(new Date(each?.created));
          newMessages.push(<TimeDividerSticky key={each.created} date={each.created} isToday={false} />);
          isFirstMessage = false;
        }
        if (lastKnownDate && condition4 && !condition3) {
          setLastKnownDate(new Date(each?.created));
          newMessages.push(<TimeDividerSticky key={each.created} date={each.created} isToday={false} />);
          isFirstMessage = false;
        }
        if (!seenToday && condition3) {
          seenToday = true;
          setLastKnownDate(new Date(each?.created));
          newMessages.push(<TimeDividerSticky key={each.created} date={each.created} isToday={true} />);
        }
        const message = {
          messageType: each?.message_type?.toUpperCase(),
          text: each?.message,
        };
        const messageUI = generateRTMUI1(
          each?.sentBy,
          each?.type,
          message,
          dp,
          each?.created,
          handlePreview
        );
        newMessages.push(messageUI);
      });
      setMessages(newMessages);
    }
  }, [chatHistory]);

  useEffect(() => {
    if (getPatientMessages) {

      getPatientMessages();

    }
    setLoading(true);
    //*Marking the messages as read for this patient
    markAsReadLogic(singlePatientData?.id)
      .then((res) => {
        if (res.status === 200) {
          setUnreadCount(0);
          //TODO: Remove unread message counter badge
          setLoading(false);
        }
      })
      .catch((e) => {
        console.log("Something went wrong while marking message as read", e);
      });

    sessionStorage.removeItem("openChatIDMini");
    sessionStorage.setItem("openChatIDMini", singlePatientData?.id?.toString());
    return () => {
      sessionStorage.removeItem("openChatIDMini");
    };
  }, []);

  useEffect(() => {
    if (inChatMessage) {
      const today = moment(getToday()).format("MMMM DD, yyyy");
      const messageUI = generateRTMUI1(
        `${singlePatientData?.first_name} ${singlePatientData?.last_name}`,
        "incoming",
        inChatMessage
      );
      if (moment(lastKnownDate).format("MMMM DD, yyyy") !== today) {
        setLastKnownDate(today);
        setMessages((prevMessages) => [
          ...prevMessages,
          <TimeDividerSticky key={today} date={today} isToday={false} />,
          messageUI,
        ]);
      } else {
        setMessages((prevMessages) => [...prevMessages, messageUI]);
      }
      setInChatMessage(null);
    }
  }, [inChatMessage]);

  useEffect(() => {
    if (clinicianChatMessage) {
      const today = moment(getToday()).format("MMMM DD, yyyy");
      const messageUI = generateRTMUI1(
        `${user?.first_name} ${user?.last_name}`,
        "outgoing",
        {
          messageType: clinicianChatMessage?.messageType.toUpperCase(),
          text: clinicianChatMessage?.text,
        }
      );
      if (moment(lastKnownDate).format("MMMM DD, yyyy") !== today) {
        setLastKnownDate(today);
        setMessages((prevMessages) => [
          ...prevMessages,
          <TimeDividerSticky key={today} date={today} isToday={false} />,
          messageUI,
        ]);
      } else {
        setMessages((prevMessages) => [...prevMessages, messageUI]);
      }
      setClinicianChatMessage(null);
    }
  }, [clinicianChatMessage]);

  return (
    <div className="mini-chat">
      {showPreview && (
        <Lightbox
          medium={previewURL}
          small={previewURL}
          large={previewURL}
          hideZoom={true}
          onClose={() => {
            setPreviewURL(null);
          }}
        />
      )}
      <div
        className="mini-chat-header"
        onClick={() => {
          if (!loading) {
            setExpanded(false);
          }
        }}
      >
        <div
          className="d-flex justify-content-evenly"
          style={{
            alignContent: "center",
            justifyItems: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <MiniChatIcon />
          <span>{chatHistory?.name || "Messages"}</span>
          {chatHistory?.unreadCount && chatHistory?.unreadCount > 0 && (
            <CBadge
              shape="rounded-circle"
              style={{
                color: "white",
                backgroundColor: "red",
                padding: "5px 8px",
              }}
            >
              {chatHistory?.unreadCount}
            </CBadge>
          )}
          <RiskPill
            custom_text={flag?.text}
            show_icon={false}
            type={flag?.type}
            styles={{ cursor: "pointer" }}
          />
        </div>
        <DownChevronIcon
          style={{ cursor: loading ? "not-allowed" : "pointer" }}
          onClick={() => {
            if (!loading) {
              setExpanded(false);
            }
          }}
        />
      </div>
      <div id="mini-chat-body" className="mini-chat-body">
        {loading && <PatientLoader />}
        {messages.map((message, index) => (
          <div key={index} ref={index === messages.length - 1 ? latestMessageRef : null}>
            {message}
          </div>
        ))}
      </div>
      {!loading && (
        <ChatInputComponent
          styles={{
            backgroundColor: "white",
            margin: "0px 10px",
            width: "96%",
          }}
          sendMessage={sendMessage}
          setClinicianChatMessage={setClinicianChatMessage}
          //clinicianId={user?.id}
          clinicianId={selectedProvider}
          peerID={singlePatientData?.id}
          patientEmail={singlePatientData?.email}
        />
      )}
    </div>
  );
};

export default StickyChat;
